import { useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { Box, Button, Card, HStack, Text, VStack, Icon, Center } from "@chakra-ui/react"
import { FaBuilding, FaPlus, FaEnvelope } from "react-icons/fa6"
import { createOrganization } from "../../auth/auth"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { useOrganizations, useUser } from "../../auth/AuthProvider"
import { PendingInvitations } from "./PendingInvitations"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import { Input } from "@chakra-ui/react"
import { CreateButton } from "@hydrologiq/shared/content/components/Button"
import { COMPANY_NAME, EMAILS } from "@hydrologiq/shared/branding"

interface NoOrganizationsEmptyStateProps {
  collapsed?: boolean
}

const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

export const NoOrganizationsEmptyState = ({ collapsed = false }: NoOrganizationsEmptyStateProps) => {
  const navigate = useNavigate()
  const user = useUser()
  const { refreshOrganizations } = useOrganizations()
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState("")
  const [slug, setSlug] = useState("")
  const [manualSlug, setManualSlug] = useState(false)

  const isPlatformAdmin = user?.role === "admin"

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newName = e.target.value
      setName(newName)
      if (!manualSlug) {
        setSlug(generateSlug(newName))
      }
    },
    [manualSlug]
  )

  const handleSlugChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setManualSlug(true)
    setSlug(e.target.value)
  }, [])

  const handleCreateOrganization = useCallback(async () => {
    if (!name.trim()) {
      toaster.error({ title: "Organisation name is required" })
      return
    }
    if (!slug.trim()) {
      toaster.error({ title: "Organisation slug is required" })
      return
    }

    setCreating(true)
    try {
      await createOrganization({
        name: name.trim(),
        slug: slug.trim(),
      })

      toaster.success({
        title: "Organisation created",
        description: "Your organisation has been created successfully",
      })

      setShowCreateDialog(false)
      setName("")
      setSlug("")
      setManualSlug(false)

      // Refresh organizations list
      await refreshOrganizations()

      // Navigate to organization settings
      navigate("/settings/organization")
    } catch (err: any) {
      toaster.error({
        title: "Failed to create organisation",
        description: err.message || "Unknown error",
      })
    } finally {
      setCreating(false)
    }
  }, [name, slug, navigate, refreshOrganizations])

  const handleInvitationAccepted = useCallback(() => {
    // Refresh organizations and reload
    refreshOrganizations().then(() => {
      window.location.reload()
    })
  }, [refreshOrganizations])

  if (collapsed) {
    return (
      <Box px={1} py={2}>
        <VStack gap={2}>
          <Icon as={FaBuilding} size="lg" color="fg.muted" />
        </VStack>
      </Box>
    )
  }

  return (
    <Center height="100vh">
      <VStack gap={6} textAlign="center" px={8} mb={"15%"}>
        <VStack gap={2}>
          <Text fontSize="2xl" fontWeight="bold">
            Account Requires Setup!
          </Text>
          <Text fontSize="lg" maxWidth="500px">
            You do not have access to the Logiq Platform yet. Please accept one of the organisation invitations below to
            get started or contact your {COMPANY_NAME} representative to request access.
          </Text>
        </VStack>
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={2} color="fg.muted">
            Pending Invitations
          </Text>
          <PendingInvitations compact onInvitationAccepted={handleInvitationAccepted} />
        </Box>
        <Button
          variant={"hydrologiq_primary" as any}
          onClick={() => (window.location.href = `mailto:${EMAILS.support}?subject=Account%20Requires%20Setup`)}
        >
          Contact Support
        </Button>
      </VStack>
    </Center>
  )
  // <Box px={3} py={4}>
  //   <VStack gap={4} align="stretch">

  //     <VStack gap={2} align="center" textAlign="center">
  //       <Icon as={FaBuilding} size="2xl" color="fg.muted" />
  //       <Text fontSize="lg" fontWeight="bold">
  //         No Organisations
  //       </Text>
  //       <Text fontSize="sm" color="fg.muted" textAlign="center">
  //         You don't have access to any organisations yet.
  //       </Text>
  //     </VStack>

  //     {/* Pending Invitations */}
  //     <Box>
  //       <Text fontSize="sm" fontWeight="medium" mb={2} color="fg.muted">
  //         Pending Invitations
  //       </Text>
  //       <PendingInvitations compact onInvitationAccepted={handleInvitationAccepted} />
  //     </Box>

  {
    /* Create Organisation Button */
  }
  {
    /* <VStack gap={2}>
          <Button colorPalette="blue" size="sm" width="full" onClick={() => setShowCreateDialog(true)}>
            <FaPlus />
            <Text ml={2}>Create Organisation</Text>
          </Button>
          {isPlatformAdmin && (
            <Button variant="outline" size="sm" width="full" onClick={() => navigate("/settings/admin")}>
              <Text>Admin Settings</Text>
            </Button>
          )}
        </VStack> */
  }
  // </VStack>

  {
    /* Create Organization Dialog */
  }
  {
    /* <Dialog
        title="Create Organization"
        open={showCreateDialog}
        setOpen={setShowCreateDialog}
        loading={creating}
        disabled={creating}
      >
        <VStack gap={4} align="stretch">
          <Box>
            <Text fontSize="sm" mb={2}>
              Organization Name
            </Text>
            <Input
              value={name}
              onChange={handleNameChange}
              placeholder="Enter organization name"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !creating) {
                  handleCreateOrganization()
                }
              }}
            />
          </Box>
          <Box>
            <Text fontSize="sm" mb={2}>
              Slug
            </Text>
            <Input value={slug} onChange={handleSlugChange} placeholder="auto-generated-slug" />
            <Text fontSize="xs" mt={1} color="gray.600">
              URL-safe identifier for the organization
            </Text>
          </Box>
          <HStack>
            <CreateButton onClick={handleCreateOrganization} loading={creating}>
              Create Organization
            </CreateButton>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
          </HStack>
        </VStack>
      </Dialog> */
  }
  // </Box>
  // )
}
